import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import { useAuthService } from '~/composables/useAuthService'
import { useRuntimeConfig } from 'nuxt/app'

const isConnected = ref(false)
const isConnecting = ref(false)
let connection = null

// True while the app intentionally tore the connection down (logout) — stops the
// onclose handler from resurrecting it.
let deliberateDisconnect = false
let restartTimer = null
let restartAttempt = 0

// Server-side presence heartbeat (UserSignalRConnections.LastSeenAt). The cleanup
// job treats >15 min of silence as a dead connection, so ping well inside that.
let heartbeatTimer = null
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000

// Map of eventName -> Set of listeners
const eventListeners = new Map()

// Retry forever with capped exponential backoff. The default array-based policy
// gives up permanently after ~47s — a laptop sleep or network switch would kill
// realtime until a full page reload.
const foreverRetryPolicy = {
    nextRetryDelayInMilliseconds: (retryContext) =>
        Math.min(30000, 1000 * 2 ** retryContext.previousRetryCount)
}

export const useSignalR = () => {
    const startHeartbeat = () => {
        stopHeartbeat()
        heartbeatTimer = setInterval(async () => {
            if (connection && connection.state === 'Connected') {
                try {
                    await connection.send('Heartbeat')
                } catch (err) {
                    console.warn('SignalR heartbeat failed:', err)
                }
            }
        }, HEARTBEAT_INTERVAL_MS)
    }

    const stopHeartbeat = () => {
        if (heartbeatTimer) {
            clearInterval(heartbeatTimer)
            heartbeatTimer = null
        }
    }

    const scheduleRestart = (hubPath) => {
        if (deliberateDisconnect || restartTimer) return
        const delay = Math.min(30000, 1000 * 2 ** restartAttempt++)
        console.info(`SignalR connection closed — restarting in ${delay}ms`)
        restartTimer = setTimeout(async () => {
            restartTimer = null
            if (!deliberateDisconnect) {
                await connect(hubPath)
            }
        }, delay)
    }

    const connect = async (hubPath) => {
        if (process.server) return

        const config = useRuntimeConfig().public.grantThomasNuxt
        const hubUrl = `${config.apiBaseUrl}${hubPath}`

        // Prevent concurrent connection attempts
        if (connection && ['Connected', 'Connecting', 'Reconnecting'].includes(connection.state)) {
            console.log('SignalR already connected or connecting, skipping...')
            return
        }

        if (isConnecting.value) {
            console.log('SignalR connection already in progress, skipping...')
            return
        }

        deliberateDisconnect = false
        isConnecting.value = true

        const { getAccessToken } = useAuthService()
        if (!getAccessToken()) {
            console.warn('No access token found for SignalR connection')
            isConnecting.value = false
            return
        }

        connection = new HubConnectionBuilder()
            // Fetch the token per negotiate so reconnects don't reuse a stale capture
            .withUrl(hubUrl, { accessTokenFactory: () => useAuthService().getAccessToken() })
            .withAutomaticReconnect(foreverRetryPolicy)
            // No withStatefulReconnect(): it makes the server hold dropped connections
            // open awaiting a resume, so closed tabs stay "online" for minutes. Missed
            // messages during drops are covered by sync-on-reconnect instead.
            .configureLogging(LogLevel.Information)
            .build()

        // Lifecycle handlers must be registered before start() so isConnected tracks
        // the real state — useMessaging watches it to sync missed messages on recovery.
        connection.onreconnecting((err) => {
            console.warn('SignalR reconnecting...', err?.message ?? '')
            isConnected.value = false
            stopHeartbeat()
        })

        connection.onreconnected(() => {
            console.info('🔄 SignalR reconnected')
            for (const eventName of eventListeners.keys()) {
                registerSignalREventDispatcher(eventName)
            }
            startHeartbeat()
            restartAttempt = 0
            isConnected.value = true
        })

        connection.onclose((err) => {
            console.warn('SignalR connection closed', err?.message ?? '')
            isConnected.value = false
            stopHeartbeat()
            // Automatic reconnect has given up (or the connection died outright) —
            // start over from scratch unless this was a deliberate disconnect.
            scheduleRestart(hubPath)
        })

        try {
            await connection.start()
            isConnecting.value = false
            restartAttempt = 0
            console.info('✅ SignalR connected')

            // Re-register master dispatchers
            for (const eventName of eventListeners.keys()) {
                registerSignalREventDispatcher(eventName)
            }

            startHeartbeat()
            // Set last: useMessaging's watcher fires sync-on-connect once handlers exist
            isConnected.value = true
        } catch (err) {
            console.error('❌ SignalR connection failed:', err)
            isConnected.value = false
            isConnecting.value = false
            // Keep trying — a page loaded while offline should connect once the
            // network returns, not stay dead until a reload.
            scheduleRestart(hubPath)
        }
    }

    const disconnect = async () => {
        deliberateDisconnect = true
        if (restartTimer) {
            clearTimeout(restartTimer)
            restartTimer = null
        }
        stopHeartbeat()
        if (connection && connection.state !== 'Disconnected') {
            await connection.stop()
            console.info('👋 SignalR disconnected')
        }
        isConnected.value = false
        isConnecting.value = false
    }

    const registerSignalREventDispatcher = (eventName) => {
        if (!connection) return
        connection.off(eventName) // prevent duplicates
        connection.on(eventName, (...args) => {
            const handlers = eventListeners.get(eventName)
            if (handlers) {
                for (const fn of handlers) {
                    try {
                        fn(...args)
                    } catch (err) {
                        console.error(`❌ Error in handler for '${eventName}'`, err)
                    }
                }
            }
        })
    }

    const on = (eventName, handler) => {
        if (!eventListeners.has(eventName)) {
            eventListeners.set(eventName, new Set())
            if (connection && connection.state === 'Connected') {
                registerSignalREventDispatcher(eventName)
            }
        }

        eventListeners.get(eventName).add(handler)
    }

    const off = (eventName, handler) => {
        if (!eventListeners.has(eventName)) return

        if (handler) {
            eventListeners.get(eventName).delete(handler)
        } else {
            eventListeners.set(eventName, new Set())
        }
    }

    const send = async (methodName, ...args) => {
        if (!connection || connection.state !== 'Connected') {
            console.warn('⚠️ Cannot send message — not connected')
            return
        }
        await connection.send(methodName, ...args)
    }

    return {
        isConnected,
        connect,
        disconnect,
        send,
        on,
        off
    }
}
